import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchRerun } from "@/lib/api";
import SpeakButton from "@/components/SpeakButton";
import { RotateCcw, ChevronRight } from "lucide-react";

/**
 * Dashboard card that surfaces one entry the user wrote ~30 days ago
 * so they can revisit their answer and speak it again. Renders nothing
 * when no candidate exists.
 */
export default function RerunCard() {
  const [rerun, setRerun] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetchRerun();
        if (r?.available) setRerun(r);
      } catch (e) {
        console.error("rerun fetch failed", e);
      }
    })();
  }, []);

  if (!rerun) return null;

  const dateLabel = (() => {
    try {
      return new Date(rerun.original_date).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  })();

  return (
    <Link
      to={`/section/${rerun.section_id}`}
      data-testid="rerun-card"
      className="block border border-obsidian-600 bg-obsidian-800/50 p-6 md:p-8 mb-12 hover:border-terracotta transition-colors group animate-fade-up"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 border border-terracotta text-terracotta flex items-center justify-center shrink-0">
            <RotateCcw className="h-4 w-4" />
          </div>
          <div>
            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
              {rerun.days_ago} days ago · {dateLabel}
            </p>
            <p className="font-sans text-[0.7rem] uppercase tracking-[0.2em] text-bone-500">
              A prompt you spoke to a month ago
            </p>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-bone-500 group-hover:text-terracotta transition-colors mt-1" />
      </div>

      <p className="font-serif italic text-lg md:text-xl text-bone-100 leading-snug mb-4">
        "{rerun.prompt_text}"
      </p>
      <div className="flex justify-end -mt-3 mb-3" onClick={(e) => e.preventDefault()}>
        <SpeakButton
          text={rerun.prompt_text}
          size="sm"
          testId="speak-rerun-prompt"
          label="Read the returning prompt aloud"
        />
      </div>
      {rerun.answer && (
        <div className="border-l-2 border-obsidian-600 pl-4 mb-4">
          <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 mb-2">
            You said
          </p>
          <p className="font-sans text-sm text-bone-300 leading-relaxed line-clamp-3">
            {rerun.answer}
          </p>
        </div>
      )}
      <p className="font-sans text-xs uppercase tracking-[0.24em] text-terracotta group-hover:text-terracotta-soft transition-colors inline-flex items-center gap-2">
        Speak it again <ChevronRight className="h-3 w-3" />
      </p>
    </Link>
  );
}
